import { html, render, TemplateResult } from "lit-html";
import { FRAAutocomplete } from "Components/fra-e-autocomplete/fra-e-autocomplete";
import { addClass, find, findAll, hasClass, removeClass } from "@kluntje/js-utils/lib/dom-helpers";
import { getCurrentMQ } from "Helper/domHelper";
import { scrollLock } from "Helper/domHelper";
import { ACTIVE } from "Helper/cssClasses";
import { getDocLocale } from "Helper/metaHelper";
import FraTimePickerField from "Components/fra-e-time-picker/fra-e-time-picker__field";
import { I18nService } from "Services/I18nService";
import { fetchJSON } from "Helper/apiHelper";

interface Suggestion {
  arrivals: {
    data: [
      {
        sched: string;
        apname: string;
        fnr: string;
        id: string;
        ac: string;
      },
    ];
    matches: number;
  };
  departures: {
    data: [
      {
        sched: string;
        apname: string;
        fnr: string;
        id: string;
        ac: string;
      },
    ];
    matches: number;
  };
  airports: {
    data: [
      {
        name: string;
        id: string;
      },
    ];
    matches: number;
  };
  airlines: {
    data: [
      {
        name: string;
        id: string;
      },
    ];
    matches: number;
  };
}

export class FRAFlightsSearchInput extends FRAAutocomplete {
  constructor() {
    super({
      ui: {
        input: ".fra-js-autocomplete__input :-one",
      },
      events: [
        {
          event: "keyup",
          target: "input",
          handler: "preventSubmitKeyup",
        },
        {
          event: "keydown",
          target: "input",
          handler: "preventSubmit",
        },
        {
          event: "keypress",
          target: "input",
          handler: "preventSubmit",
        },
      ],
    });
  }
  afterComponentRender() {
    FRAAutocomplete.INPUT_MIN_LENGTH = 2;
  }

  /**
   * @override
   */
  get preserveListBlurAttribute() {
    // only close >mq3 on focus lost
    return getCurrentMQ() === "MQ1" || getCurrentMQ() === "MQ2" || getCurrentMQ() === "MQ3";
  }

  /**
   * @override
   * Render suggestions in list
   * @param {Array<Suggestion>} suggestions
   */
  renderSuggestions(suggestions: Suggestion): void {
    // check if results are available
    const autoSuggestResults = this.getAutosuggestResults(suggestions);

    if (this.state.loaded) {
      this.classList.toggle("fra-m-flights-search-input--has-results", true);
      if (autoSuggestResults.length > 0) {
        render(autoSuggestResults, this.ui.autocompleteList);
      } else {
        render(this.renderNoResults(), this.ui.autocompleteList);
      }
      this.showAutocompleteList();
    }
  }

  getAutosuggestResults(suggestions: Suggestion) {
    const autoSuggestResults: any = new Array<TemplateResult>();

    if (suggestions.airlines || suggestions.airports || suggestions.arrivals || suggestions.departures) {
      const searchType = this.getAttribute("data-flighttype");
      const flights = searchType === "arrivals" ? suggestions.arrivals : suggestions.departures;

      // show up to 4 flights
      if (
        flights &&
        flights.data.length > 0 &&
        ((suggestions.airlines && suggestions.airlines.data.length > 0) ||
          (suggestions.airports && suggestions.airports.data.length > 0))
      ) {
        autoSuggestResults.push(this.renderFlights(flights.data));
        // toggle show more
        if (flights.matches > 4) {
          autoSuggestResults.push(html`
            <li class="fra-e-autocomplete__list-item">${this.renderShowMoreButton()}</li>
          `);
        }
        autoSuggestResults.push(
          html`
            <div class="fra-e-autocomplete__list-item--separation"</div>
          `,
        );
        // show up to 8 flights
      } else if (flights && flights.data.length > 0) {
        autoSuggestResults.push(this.renderFlights(flights.data, 8));
      }

      if (suggestions.airlines && suggestions.airlines.matches > 4) {
        autoSuggestResults.push(html`
          <div class="fra-e-autocomplete__grid">${this.renderAirlines(suggestions.airlines.data).slice(0, 5)}</div>
        `);
        autoSuggestResults.push(html`
          <li class="fra-e-autocomplete__list-item">${this.renderShowMoreButton()}</li>
        `);
      } else if (suggestions.airlines && suggestions.airlines.data.length > 0) {
        autoSuggestResults.push(html`
          <div class="fra-e-autocomplete__grid">${this.renderAirlines(suggestions.airlines.data)}</div>
        `);
      }

      if (
        suggestions.airlines &&
        suggestions.airlines.data.length > 0 &&
        suggestions.airports &&
        suggestions.airports.data.length > 0
      ) {
        autoSuggestResults.push(
          html`
            <div class="fra-e-autocomplete__list-item--separation"</div>
          `,
        );
      }

      if (suggestions.airports && suggestions.airports.matches > 4) {
        autoSuggestResults.push(html`
          <div class="fra-e-autocomplete__grid">${this.renderAirports(suggestions.airports.data).slice(0, 5)}</div>
        `);
        autoSuggestResults.push(html`
          <li class="fra-e-autocomplete__list-item">${this.renderShowMoreButton()}</li>
        `);
      } else if (suggestions.airports && suggestions.airports.data.length > 0) {
        autoSuggestResults.push(html`
          <div class="fra-e-autocomplete__grid">${this.renderAirports(suggestions.airports.data).slice(0, 5)}</div>
        `);
      }
    }
    return autoSuggestResults;
  }

  renderNoResults(): TemplateResult {
    const headline = I18nService.getInstance().getKey(
      "fraport.travel.components.content.flight.search.input.noResults.label",
    );
    const buttonLabel = I18nService.getInstance().getKey(
      "fraport.travel.components.content.flight.search.input.noResults.button.label",
    );
    const overviewUrl = this.getAttribute(`data-overview-autosuggest-${this.getAttribute("data-flighttype")}`);
    return html`
      <span class="fra-e-autocomplete__no-results--headline">${headline}</span>
      <a class="fra-e-link fra-e-link--with-icon fra-e-link--animated-right" href="${overviewUrl}" target="_self">
        <span class="fra-e-link__label">${buttonLabel}</span>
        <fra-e-icon class="fra-e-icon " icon-id="fra-icon-arrow-right"></fra-e-icon>
      </a>
    `;
  }

  renderShowMoreButton() {
    return html`
      <a
        class="fra-e-link fra-e-link--with-icon fra-e-link--animated-right"
        href="#"
        target="_self"
        @click=${() => {
          this.dispatchEvent(new CustomEvent("fra-show-more-results"));
          this.collapse();
        }}
      >
        <span class="fra-e-link__label">
          ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.search.input.showMore.label")}
        </span>
        <fra-e-icon class="fra-e-icon " icon-id="fra-icon-arrow-right"></fra-e-icon>
      </a>
    `;
  }

  renderFlights(flights: Array<any>, amount = 4): Array<TemplateResult> {
    let flightResults = flights.slice(0, amount).map(({ sched, apname, fnr, id, ac }) => {
      const time = new Date(sched).toLocaleTimeString(getDocLocale(), { hour: "2-digit", minute: "2-digit" });
      return html`
        <li class="fra-e-autocomplete__list-item fra-e-autocomplete__result" id=${id} type=${ac}>
          <a
            class="fra-e-link fra-js-suggestion-link"
            @click=${() => {
              removeClass(this, ACTIVE);
              document.location.href = this.getDetailPageUrl(id, ac);
            }}
          >
            <span class="fra-e-link__label fra-e-autocomplete__flight-grid">
              <div class="fra-e-autocomplete__flight-grid--time-name">
                <span>${time}</span>
                <span>${apname}</span>
              </div>
              <span class="fra-e-autocomplete__flight-grid--number">${fnr}</span>
            </span>
          </a>
        </li>
      `;
    });

    flightResults = this.addFlightDates(flights.slice(0, amount), flightResults);
    return flightResults;
  }

  addFlightDates(flights: Array<any>, results: TemplateResult[]): TemplateResult[] {
    let dateIndex = 0;
    let count = 0;
    results.unshift(this.renderDate(new Date(flights[0].sched).toLocaleDateString()));
    flights.forEach((flight, index) => {
      if (new Date(flight.sched).getDate() !== new Date(flights[dateIndex].sched).getDate()) {
        dateIndex = index;
        count++;
        results.splice(index + count, 0, this.renderDate(new Date(flight.sched).toLocaleDateString()));
      }
    });
    return results;
  }

  renderDate(date: string): TemplateResult {
    return html`
      <li class="fra-e-autocomplete__list-item fra-e-autocomplete__list-heading">
        <span>
          ${I18nService.getInstance().getKey(
            "fraport.travel.components.content.flight.search.input.flightsHeader.label",
          )}
          ${date}
        </span>
      </li>
    `;
  }

  renderAirlines(airlines: Array<any>): Array<TemplateResult> {
    const airlineResults = airlines.map(({ name, id }) => {
      return html`
        <li class="fra-e-autocomplete__list-item fra-e-autocomplete__result">
          <a
            class="fra-e-link fra-js-suggestion-link"
            @click=${() => {
              this.dispatchEvent(
                new CustomEvent("fra-set-search-to-suggestion", {
                  detail: {
                    q: name,
                    key: id,
                    type: "Airline",
                  },
                }),
              );
              removeClass(this, ACTIVE);
            }}
          >
            <span class="fra-e-link__label">${name}</span>
          </a>
        </li>
      `;
    });
    airlineResults.unshift(html`
      <li class="fra-e-autocomplete__list-item fra-e-autocomplete__list-heading">
        <span>
          ${I18nService.getInstance().getKey(
            "fraport.travel.components.content.flight.search.input.airlinesHeader.label",
          )}
        </span>
      </li>
    `);
    return airlineResults;
  }

  renderAirports(airports: Array<any>): Array<TemplateResult> {
    const airportsResults = airports.map(({ name, id }) => {
      return html`
        <li class="fra-e-autocomplete__list-item fra-e-autocomplete__result">
          <a
            class="fra-e-link fra-js-suggestion-link"
            @click=${() => {
              this.dispatchEvent(
                new CustomEvent("fra-set-search-to-suggestion", {
                  detail: {
                    q: name,
                    type: "Airport",
                    key: id,
                  },
                }),
              );
              removeClass(this, ACTIVE);
            }}
          >
            <span class="fra-e-link__label">${name}</span>
          </a>
        </li>
      `;
    });
    airportsResults.unshift(html`
      <li class="fra-e-autocomplete__list-item fra-e-autocomplete__list-heading">
        <span>
          ${I18nService.getInstance().getKey(
            "fraport.travel.components.content.flight.search.input.airportsHeader.label",
          )}
        </span>
      </li>
    `);
    return airportsResults;
  }

  getDetailPageUrl(id: string, ac: string): string {
    const flightType = this.getAttribute("data-flighttype");
    let url: string | null = "";
    if (flightType === "departures" && ac !== "TRS") {
      url = this.getAttribute("data-flight-departures-details-url");
    } else if (flightType === "departures" && ac === "TRS") {
      url = this.getAttribute("data-train-departures-details-url");
    } else if (flightType === "arrivals" && ac === "TRS") {
      url = this.getAttribute("data-train-arrivals-details-url");
    } else {
      url = this.getAttribute("data-flight-arrivals-details-url");
    }
    return `${url}/${id}.html`;
  }

  /**
   * @override
   * removed data wrapper
   */
  async fetchSuggestions(keyword: string) {
    await fetchJSON(this.generateRequest(keyword))
      .then(response => {
        this.setState({ loaded: true });
        this.renderSuggestions(response);
      })
      .catch(error => {
        console.warn(`An error occurred while loading the component: ${error}`);
        return "";
      });
  }

  /**
   * @override
   */
  // @ts-ignore
  async handleInputFocus(e: any) {
    this.handleSearchInput(e);
    if (getCurrentMQ() === "MQ1" || getCurrentMQ() === "MQ2") {
      scrollLock.pageBodyLock();
      super.handleInputFocus();
      // needed for ios to keep input in view after keyboard pops up
      await new Promise(res => setTimeout(res, 200));
      document.scrollingElement!.scrollTop = 0;
    } else {
      super.handleInputFocus();
    }
  }

  /**
   * @override
   * always show reset button on mobile
   */
  hideResetBtn(): void {
    if (getCurrentMQ() === "MQ1" || getCurrentMQ() === "MQ2") {
      return;
    }
    super.hideResetBtn();
  }

  /**
   * @override
   * reset input to default state and cause a new search with default values
   */
  resetInput(): void {
    this.ui.input.value = "";
    this.hideResetBtn();
    this.dispatchEvent(new CustomEvent("fra-set-search-to-suggestion", { detail: { key: "", type: "", q: "" } }));
    removeClass(this, ACTIVE);
    this.collapse();
  }

  /**
   * @override
   */
  showAutocompleteList(): void {
    super.showAutocompleteList();
    this.classList.add("fra-m-flights-search-input--results-shown");
  }

  /**
   * @override
   */
  hideAutocompleteList(): void {
    super.hideAutocompleteList();
    this.classList.remove("fra-m-flights-search-input--results-shown");
  }

  /**
   * @override
   */
  handleSearchInput(e: any): void {
    switch (e.which) {
      case 38: // up
        this.selectResult(-1);
        break;

      case 40: // down
        this.selectResult(1);
        break;

      default:
        super.handleSearchInput(e);
    }
  }

  // necessary to enable keyboard selection of suggestions
  preventSubmit(e: any) {
    if (e.keyCode === 13) {
      this.checkSelection(e);
    }
  }

  preventSubmitKeyup(e: any) {
    if (e.keyCode === 13) {
      this.checkSelection(e);
    } else {
      this.handleSearchInput(e);
    }
  }

  checkSelection(e: any) {
    const selectedElement = find(this, ".fra-e-autocomplete__result--selected");
    if (selectedElement && !/^\d/.test(selectedElement.innerText)) {
      this.ui.input.value = selectedElement.innerText.trim();
      this.collapse();
    } else if (selectedElement) {
      const id = selectedElement.id;
      // @ts-ignore
      document.location.href = this.getDetailPageUrl(id, selectedElement.type);
    }
    this.collapse();
    e.preventDefault();
  }

  selectResult(direction: number) {
    const allResults = findAll(this, ".fra-e-autocomplete__result");
    let index = -1;
    allResults.forEach((item, i) => {
      if (hasClass(item, "fra-e-autocomplete__result--selected")) {
        index = i;
        removeClass(item, "fra-e-autocomplete__result--selected");
      }
    });
    if (index === 0 && direction === 1) {
      addClass(allResults[1], "fra-e-autocomplete__result--selected");
    } else if (index > 0) {
      addClass(allResults[index + direction], "fra-e-autocomplete__result--selected");
    } else if (index === -1 && direction === 1) {
      addClass(allResults[0], "fra-e-autocomplete__result--selected");
    } else {
      removeClass(allResults[index], "fra-e-autocomplete__result--selected");
    }
  }

  /**
   * @override
   * Generate request to fetch data from
   * @param {string} keyword
   * @returns {string}
   */
  generateRequest(keyword: string): string {
    const flighttype = this.getAttribute("data-flighttype");
    const lang = getDocLocale().split("-")[0];
    const [selectedHour, selectedMinutes] = FraTimePickerField.value.split(":");
    const flightSearchForm = this.closest(".fra-m-flights-search__form");
    // @ts-ignore
    const datepickerValue = flightSearchForm!.querySelector(".fra-e-datetime-picker__field")!.value;
    const dateTimepickerValue = new Date(datepickerValue).setHours(Number(selectedHour), Number(selectedMinutes));
    const time = new Date(dateTimepickerValue).toISOString();
    const keywordUrl = `?q=${keyword}&lang=${lang}&flighttype=${flighttype}&time=${time}`;
    return `${this.apiUrl}${keywordUrl}`;
  }
}

customElements.define("fra-m-flights-search-input", FRAFlightsSearchInput);

export default FRAFlightsSearchInput;
